import groq from 'groq'

import { blogPostWithoutBodyFragment } from './blog'
import {
  complexPortableTextFragment,
  simplePortableTextFragment,
} from './content'
import { imageFragment } from './image'
import { productFragment } from './product'
import { newsletterFragment } from './site'
import { videoFragment } from './video'

const freeformBlockFragment = groq`
  ...,
  content[] {
    ${complexPortableTextFragment}
  },
`

const accordionsBlockFragment = groq`
  items[] {
    ...,
    content[] {
      ${simplePortableTextFragment}
    },
  },
`

const productCardBlockFragment = groq`
  product->{
    ${productFragment}
  },
`

const signupFormStringsFragment = groq`
  signupFirstName,
  signupFirstNamePlaceholder,
  signupFirstNameMissing,
  signupLastName,
  signupLastNamePlaceholder,
  signupLastNameMissing,
  signupEmail,
  signupEmailPlaceholder,
  signupEmailMissing,
  signupPassword,
  signupPasswordPlaceholder,
  signupPasswordMissing,
  signupErrorMessage[] {
    ${complexPortableTextFragment}
  },
  signupSubmit,
`

const signupFormBlockFragment = groq`
  active,
  "signupFormStrings": *[
    _type == "strings" &&
    locale == $locale
  ][0] {
    ${signupFormStringsFragment}
  },
`

const loginFormStringsFragment = groq`
  signupEmail,
  signupEmailPlaceholder,
  signupEmailMissing,
  signupPassword,
  signupPasswordPlaceholder,
  signupPasswordMissing,
  loginErrorMessage[] {
    ${complexPortableTextFragment}
  },
  loginFailedMessage[] {
    ${complexPortableTextFragment}
  },
  loginSubmit,
`

const loginFormBlockFragment = groq`
  active,
  "loginFormStrings": *[
    _type == "strings" &&
    locale == $locale
  ][0] {
    ${loginFormStringsFragment}
  },
`

const passwordRecoveryFormStringsFragment = groq`
  signupEmail,
  signupEmailPlaceholder,
  signupEmailMissing,
  passwordRecoveryErrorMessage[] {
    ${complexPortableTextFragment}
  },
  passwordRecoverySuccessMessage[] {
    ${complexPortableTextFragment}
  },
  passwordRecoverySubmit,
`

const passwordRecoveryFormBlockFragment = groq`
  active,
  "passwordRecoveryFormStrings": *[
    _type == "strings" &&
    locale == $locale
  ][0] {
    ${passwordRecoveryFormStringsFragment}
  },
`

const accountAddressDetailsStringsFragment = groq`
  accountAddAddressHeading,
  accountAddAddress,
  accountDefaultAddressLabel,
`

const addressFormStringsFragment = groq`
  account,
  accountAddAddressHeading,
  accountEditAddressHeading,
  accountFirstName,
  accountFirstNamePlaceholder,
  accountFirstNameMissing,
  accountLastName,
  accountLastNamePlaceholder,
  accountLastNameMissing,
  accountCompany,
  accountCompanyPlaceholder,
  accountCompanyMissing,
  accountAddressLine1,
  accountAddressLine1Placeholder,
  accountAddressLine1Missing,
  accountAddressLine2,
  accountAddressLine2Placeholder,
  accountCity,
  accountCityPlaceholder,
  accountCityMissing,
  accountCountry,
  accountCountryPlaceholder,
  accountCountryMissing,
  accountZip,
  accountZipPlaceholder,
  accountZipMissing,
  accountPhone,
  accountPhonePlaceholder,
  accountPhoneMissing,
  accountSetAsDefault,
  addressErrorMessage[] {
    ${complexPortableTextFragment}
  },
  accountAddAddress,
  accountEditAddress,
`

const accountAddressDetailsBlockFragment = groq`
  active,
  "accountAddressDetailsStrings": *[
    _type == "strings" &&
    locale == $locale
  ][0] {
    ${accountAddressDetailsStringsFragment}
  },
  "addressFormStrings": *[
    _type == "strings" &&
    locale == $locale
  ][0] {
    ${addressFormStringsFragment}
  },
`

const accountDetailsStringsFragment = groq`
  accountAccountDetailsHeading,
  accountViewAddresses,
  accountViewSubscriptions,
`

const accountDetailsBlockFragment = groq`
  active,
  "accountDetailsStrings": *[
    _type == "strings" &&
    locale == $locale
  ][0] {
    ${accountDetailsStringsFragment}
  },
`

const accountProductListStringsFragment = groq`
  accountProductListHeading,
  accountProductListEmpty[] {
    ${complexPortableTextFragment}
  },
  accountProductListProduct,
  accountProductListViewProduct,
`

const accountProductListBlockFragment = groq`
  active,
  "accountProductListStrings": *[
    _type == "strings" &&
    locale == $locale
  ][0] {
    ${accountProductListStringsFragment}
  },
`

const accountOrderListStringsFragment = groq`
  accountOrderListPaymentStatusAuthorized,
  accountOrderListPaymentStatusPaid,
  accountOrderListPaymentStatusPartiallyPaid,
  accountOrderListPaymentStatusPartiallyRefunded,
  accountOrderListPaymentStatusPending,
  accountOrderListPaymentStatusRefunded,
  accountOrderListPaymentStatusVoided,
  accountOrderListFulfillmentStatusFulfilled,
  accountOrderListFulfillmentStatusInProgress,
  accountOrderListFulfillmentStatusOnHold,
  accountOrderListFulfillmentStatusOpen,
  accountOrderListFulfillmentStatusPartiallyFulfilled,
  accountOrderListFulfillmentStatusPendingFulfillment,
  accountOrderListFulfillmentStatusRestocked,
  accountOrderListFulfillmentStatusScheduled,
  accountOrderListFulfillmentStatusUnfulfilled,
  accountOrderListHeading,
  accountOrderListOrder,
  accountOrderListDate,
  accountOrderListPaymentStatus,
  accountOrderListFulfillmentStatus,
  accountOrderListTotal,
  accountOrderDetailsHeading,
  accountOrderDetailsDate,
  accountOrderDetailsProduct,
  accountOrderDetailsSku,
  accountOrderDetailsPrice,
  accountOrderDetailsQuantity,
  accountOrderDetailsTotal,
  accountOrderDetailsSubtotal,
  accountOrderDetailsShipping,
  accountOrderDetailsTax,
  accountOrderDetailsBillingAddress,
  accountOrderDetailsShippingAddress,
  accountOrderListEmpty[] {
    ${complexPortableTextFragment}
  },
`

const accountOrderListBlockFragment = groq`
  active,
  "accountOrderListStrings": *[
    _type == "strings" &&
    locale == $locale
  ][0] {
    ${accountOrderListStringsFragment}
  },
`

const collectionCarouselBlockFragment = groq`
  collection->{
    products[]->{
      ${productFragment}
    },
  },
`

const productBundleFormBlockFragment = groq`
  showGallery,
  productBundle->{
    name,
    slots[] {
      name,
      showSelection,
      label,
      "selectedOptionNames": products[] {
        "productId": selection.id,
        "list": selection.options[selected == true].name,
      },
      "products": *[
        _type == "product" &&
        _id in ^.products[].selection.id &&
        locale == $locale &&
        !wasDeleted
      ] {
        ${productFragment}
      },
    },
    variantMap[] {
      products[] {
        "id": selection.id,
        "variantIds": selection.variants[selected == true].id,
      },
    },
    gallery[] {
      variantCombination,
      photos[] {
        ${imageFragment}
      },
    },
  },
`

const blogPostCardBlockFragment = groq`
  options,
  post->{
    ${blogPostWithoutBodyFragment}
  },
`

const contentCarouselBlockFragment = groq`
  items[] {
    _key,
    _type,
    _type == "freeform" => {
      ${freeformBlockFragment}
    },
    _type == "photo" => {
      ${imageFragment}
    },
    _type == "product" => {
      "product": *[
        _type == "product" &&
        _id == ^._ref
      ][0] {
        ${productFragment}
      },
    },
    _type == "blogPost" => {
      "blogPost": *[
        _type == "blogPost" &&
        _id == ^._ref
      ][0] {
        ${blogPostWithoutBodyFragment}
      },
    },
    _type == "video" => {
      ${videoFragment}
    },
  },
`

const calendlyWidgetFragment = groq`
  ...,
`

const cookieDeclarationFragment = groq`
  active,
  "cookieBotId": *[
    _type == "generalSettings" &&
    locale == $locale
  ][0].cookieBotId,
`

export const demoFormFragment = groq`
  ...,
  successMsg[] {
    ${complexPortableTextFragment}
  },
  errorMsg[] {
    ${complexPortableTextFragment}
  },
  terms[] {
    ${simplePortableTextFragment}
  },
  newsletterStatement[] {
    ${simplePortableTextFragment}
  },
`

const iframeBlockFragment = groq`
  ...,
`

const teamMemberCardFragment = groq`
  teamMember->{
    ...,
    blogAuthor->{
      slug,
    },
    photo {
      ${imageFragment}
    },
  },
`

const contactFormFragment = groq`
  ...,
  successMsg[] {
    ${complexPortableTextFragment}
  },
  errorMsg[] {
    ${complexPortableTextFragment}
  },
  terms[] {
    ${simplePortableTextFragment}
  },
`

const baseBlockFragment = groq`
  _key,
  _type,
  _type == "freeform" => {
    ${freeformBlockFragment}
  },
  _type == "accordions" => {
    ${accordionsBlockFragment}
  },
  _type == "productCard" => {
    ${productCardBlockFragment}
  },
  _type == "signupForm" => {
    ${signupFormBlockFragment}
  },
  _type == "loginForm" => {
    ${loginFormBlockFragment}
  },
  _type == "passwordRecoveryForm" => {
    ${passwordRecoveryFormBlockFragment}
  },
  _type == "accountDetails" => {
    ${accountDetailsBlockFragment}
  },
  _type == "accountAddressDetails" => {
    ${accountAddressDetailsBlockFragment}
  },
  _type == "accountProductList" => {
    ${accountProductListBlockFragment}
  },
  _type == "accountOrderList" => {
    ${accountOrderListBlockFragment}
  },
  _type == "video" => {
    ${videoFragment}
  },
  _type == "collectionCarousel" => {
    ${collectionCarouselBlockFragment}
  },
  _type == "newsletter" => {
    ${newsletterFragment}
  },
  _type == "productBundleForm" => {
    ${productBundleFormBlockFragment}
  },
  _type == "blogPostCard" => {
    ${blogPostCardBlockFragment}
  },
  _type == "contentCarousel" => {
    ${contentCarouselBlockFragment}
  },
  _type == "calendlyWidget" => {
    ${calendlyWidgetFragment}
  },
  _type == "cookieDeclaration" => {
    ${cookieDeclarationFragment}
  },
  _type == "demoForm" => {
    ${demoFormFragment}
  },
  _type == "iframe" => {
    ${iframeBlockFragment}
  },
  _type == "teamMemberCard" => {
    ${teamMemberCardFragment}
  },
  _type == "contactForm" => {
    ${contactFormFragment}
  },
 `

const predefinedContentBlockFragment = groq`
  "contentBlocks": contentBlock->contentBlocks[] {
    ${baseBlockFragment}
  },
`

export const blockFragment = groq`
  ${baseBlockFragment}
  _type == 'predefinedContentBlock' => {
    ${predefinedContentBlockFragment}
  },
 `
